import { useEffect } from 'react';
import { useInstitutionData } from '../contexts/InstitutionDataContext';
import { useAuth } from '../contexts/AuthContext';

/**
 * ThemeHandler se encarga de inyectar las variables CSS (--color-primary, etc.)
 * basándose en el color elegido en la configuración de la institución,
 * y aplica el tema oscuro/claro según la preferencia del usuario.
 */
export default function ThemeHandler() {
  const { institution } = useInstitutionData();
  const { user, loading } = useAuth();

  useEffect(() => {
    // Evitar sobreescribir el tema inyectado por index.html mientras AuthContext carga
    if (loading) return;

    // Color por defecto (Verde Institucional Premium)
    const primary = institution?.primaryColor || '#179646';
    
    // Calcular variaciones
    const dark = darkenColor(primary, 0.15);
    const light = hexToRgba(primary, 0.1);
    const lightMedium = hexToRgba(primary, 0.15);

    const root = document.documentElement;

    let isDark = false;
    if (user && user.theme) {
      isDark = user.theme === 'dark';
    } else {
      const cached = localStorage.getItem('userTheme');
      if (cached) {
        isDark = cached === 'dark';
      } else if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        isDark = true;
      }
    }

    // Aplicar Modo Oscuro y colores de alto contraste si el usuario lo prefiere (o el sistema/caché)
    if (isDark) {
      root.setAttribute('data-theme', 'dark');
      
      // Paleta fija curada para Modo Oscuro (Amarillo tipo Luna)
      const darkPrimary = '#facc15'; 
      root.style.setProperty('--color-primary', darkPrimary);
      root.style.setProperty('--color-primary-dark', '#eab308');
      root.style.setProperty('--color-primary-text', '#1a1a1a');
      root.style.setProperty('--color-primary-light', 'rgba(250, 204, 21, 0.15)');
      root.style.setProperty('--color-primary-light-medium', 'rgba(250, 204, 21, 0.25)');
      
    } else {
      root.removeAttribute('data-theme');
      root.style.backgroundColor = '';
      if (document.body) document.body.style.backgroundColor = '';
      
      // Colores normales de la institución
      root.style.setProperty('--color-primary', primary);
      root.style.setProperty('--color-primary-dark', dark);
      root.style.setProperty('--color-primary-text', 'white');
      root.style.setProperty('--color-primary-light', light);
      root.style.setProperty('--color-primary-light-medium', lightMedium);
    }

  }, [institution?.primaryColor, user?.theme, user, loading]);

  return null; // Componente lógico, no renderiza nada
}

// --- Utilidades de Color ---

function hexToRgba(hex, alpha) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return hex;
  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function darkenColor(hex, percent) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return hex;
  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);
  
  const f = (c) => Math.max(0, Math.min(255, Math.floor(c * (1 - percent))));
  return `rgb(${f(r)}, ${f(g)}, ${f(b)})`;
}
